import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft, CheckCircle, Clock, Code2, Loader2, MinusCircle, RotateCcw, Trophy, XCircle,
} from "lucide-react";
import apiClient from "../../utils/apiClient";
import {
  AppButton, AppCard, AppCardContent, AppCardHeader, AppCardTitle, AppDataTable, Callout, EmptyState,
  MathText, StatsCard, StatsGrid, StatusBadge, cn,
} from "../ui";
import Shimmer from "../Common/Shimmer";
import RichText from "../Common/RichText";
import { PassageView, passageRanges, rangeLabel } from "../QuizPassage";
import {
  CODING_STATE_LABELS, PENDING_POLL_LIMIT_MS, PENDING_POLL_MS, VERDICT_TONES, formatDuration, plural,
  reviewResult, verdictLabel,
} from "./studentAssessmentRules";

const TEXT_TYPES = ["text_answer", "fill_blank", "numeric_answer"];

/** A block of program text — code, a test's input, an expected output. */
function CodeBlock({ label, children }) {
  return (
    <div className="tw-flex tw-min-w-0 tw-flex-col tw-gap-1">
      {label && <span className="tw-text-xs tw-font-medium tw-text-content-muted">{label}</span>}
      {/* Focusable so a keyboard user can scroll a long line sideways. */}
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex */}
      <pre tabIndex={0} className="tw-m-0 tw-max-h-80 tw-overflow-auto tw-rounded-sm tw-border tw-border-solid tw-border-code-border tw-bg-code tw-p-3 tw-font-mono tw-text-xs tw-text-code-fg tw-focus-ring">
        {children === "" || children == null ? <span className="tw-text-code-fg-muted">(empty)</span> : children}
      </pre>
    </div>
  );
}

/**
 * A coding answer in the review: the problem, the student's code, and each
 * test's verdict. A hidden test is a verdict and nothing else — the server
 * never sends its input or expected output.
 */
function CodingReview({ coding }) {
  let sample = 0;
  let hidden = 0;
  const judged = coding.state === "done";
  return (
    <div className="tw-flex tw-flex-col tw-gap-3">
      <RichText value={coding.statement} compact />
      <CodeBlock label={`Your code${coding.language ? ` · ${coding.language}` : ""}`}>{coding.code}</CodeBlock>
      {judged && (
        <p className="tw-m-0 tw-text-sm tw-font-semibold tw-text-content">
          {coding.passed} of {plural(coding.total, "test")} passed
        </p>
      )}
      {!judged && coding.state === "pending" && (
        <p className="tw-m-0 tw-text-sm tw-text-content-muted">Your code is still being checked.</p>
      )}
      {coding.message && <CodeBlock label="Message">{coding.message}</CodeBlock>}
      {(coding.tests || []).length > 0 && (
        <ul className="tw-m-0 tw-flex tw-list-none tw-flex-col tw-gap-2 tw-p-0">
          {coding.tests.map((t, i) => {
            const name = t.hidden ? `Hidden test ${(hidden += 1)}` : `Sample ${(sample += 1)}`;
            return (
              <li key={i} className="tw-flex tw-flex-col tw-gap-2 tw-rounded-md tw-border tw-border-solid tw-border-line tw-px-3 tw-py-2">
                <div className="tw-flex tw-items-center tw-justify-between tw-gap-2">
                  <span className="tw-text-[0.8125rem] tw-font-medium tw-text-content">{name}</span>
                  <StatusBadge status={VERDICT_TONES[t.verdict] || "neutral"}>{verdictLabel(t.verdict)}</StatusBadge>
                </div>
                {!t.hidden && (t.input != null || t.expectedOutput != null) && (
                  <div className="tw-grid tw-gap-2 min-[768px]:tw-grid-cols-2">
                    {t.input != null && <CodeBlock label="Input">{t.input}</CodeBlock>}
                    {t.expectedOutput != null && <CodeBlock label="Expected output">{t.expectedOutput}</CodeBlock>}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

const OPTION_TONE = {
  correct: "tw-border-success/40 tw-bg-success-soft tw-text-success-fg",
  wrong: "tw-border-danger/40 tw-bg-danger-soft tw-text-danger-fg",
  chosen: "tw-border-primary/40 tw-bg-primary-soft tw-text-content",
  neutral: "tw-border-line-soft tw-bg-transparent tw-text-content",
};

/** A question in the review, with what the student answered. */
function ReviewQuestion({ question, answer, index, showCorrect, showExplanation }) {
  const result = reviewResult(question, answer);
  const selected = (answer?.selectedOptionIds || []).map(String);
  const toneOf = (opt) => {
    const picked = selected.includes(String(opt._id));
    // Without the answer key the student's pick is only that — theirs.
    if (!showCorrect) return picked ? "chosen" : "neutral";
    if (opt.isCorrect) return "correct";
    return picked ? "wrong" : "neutral";
  };

  return (
    <article aria-label={`Question ${index + 1}`} className="tw-flex tw-flex-col tw-gap-3 tw-py-4 tw-border-0 tw-border-t tw-border-solid tw-border-line-soft first:tw-border-t-0">
      <header className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-2">
        <span className="tw-text-[0.8125rem] tw-font-semibold tw-text-content-muted">
          Q{index + 1} · {plural(question.marks || 1, "mark")}
          {question.questionType === "coding" && " · Coding problem"}
        </span>
        <StatusBadge status={result.tone}>{result.label}</StatusBadge>
      </header>

      <MathText as="div" text={question.questionText} className="tw-whitespace-pre-wrap tw-text-sm tw-font-medium tw-text-content" />

      {question.imageUrl && (
        <img
          src={question.imageUrl}
          alt=""
          className="tw-block tw-max-h-80 tw-max-w-full tw-rounded-sm tw-border tw-border-solid tw-border-line tw-object-contain"
        />
      )}
      {question.codeSnippet && <CodeBlock>{question.codeSnippet}</CodeBlock>}

      {question.questionType === "coding" && question.coding && <CodingReview coding={question.coding} />}

      {question.questionType !== "coding" && question.options?.length > 0 && (
        <ul className="tw-m-0 tw-flex tw-list-none tw-flex-col tw-gap-1.5 tw-p-0">
          {question.options.map((opt) => {
            const tone = toneOf(opt);
            const picked = selected.includes(String(opt._id));
            return (
              <li key={opt._id} className={cn("tw-flex tw-items-start tw-justify-between tw-gap-3 tw-rounded-sm tw-border tw-border-solid tw-px-3 tw-py-2 tw-text-[0.8125rem]", OPTION_TONE[tone])}>
                <span className="tw-flex tw-min-w-0 tw-flex-col tw-gap-2">
                  {opt.text && <MathText text={opt.text} />}
                  {opt.imageUrl && <img src={opt.imageUrl} alt="" className="tw-block tw-max-h-40 tw-max-w-full tw-rounded-sm tw-object-contain" />}
                </span>
                {(picked || tone === "correct") && (
                  <span className="tw-shrink-0 tw-text-xs tw-font-semibold">
                    {[picked && "Your answer", tone === "correct" && "Correct answer"].filter(Boolean).join(" · ")}
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {TEXT_TYPES.includes(question.questionType) && (
        <p className="tw-m-0 tw-rounded-sm tw-border tw-border-solid tw-border-line-soft tw-px-3 tw-py-2 tw-text-[0.8125rem] tw-text-content">
          Your answer: {answer?.textAnswer || "(empty)"}
        </p>
      )}

      {showExplanation && question.explanation && (
        <div className="tw-rounded-sm tw-bg-surface-sunken tw-px-3 tw-py-2.5 tw-text-[0.8125rem] tw-leading-relaxed tw-text-content-muted">
          <strong className="tw-text-content">Explanation:</strong> {question.explanation}
        </div>
      )}
    </article>
  );
}

function SectionsTable({ sections }) {
  return (
    <AppCard as="section" aria-label="By section">
      <AppCardHeader>
        <AppCardTitle as="h2">By section</AppCardTitle>
      </AppCardHeader>
      <AppDataTable
        fill={false}
        rows={sections.map((s, i) => ({ ...s, key: `${s.title}-${i}` }))}
        rowKey={(s) => s.key}
        columns={[
          { key: "title", label: "Section", render: (s) => <span className="tw-font-medium tw-text-content">{s.title}</span> },
          { key: "answered", label: "Answered", align: "right", render: (s) => <span className="tw-tabular-nums">{s.answered}/{s.questionCount}</span> },
          { key: "score", label: "Score", align: "right", render: (s) => <strong className="tw-tabular-nums">{s.score}/{s.totalMarks}</strong> },
        ]}
      />
    </AppCard>
  );
}

function CodingCard({ coding }) {
  return (
    <AppCard as="section" aria-label="Coding problems">
      <AppCardHeader>
        <AppCardTitle as="h2"><Code2 size={16} aria-hidden="true" /> Coding problems</AppCardTitle>
      </AppCardHeader>
      <AppDataTable
        fill={false}
        rows={coding.map((c, i) => ({ ...c, key: String(c.question || i) }))}
        rowKey={(c) => c.key}
        columns={[
          { key: "title", label: "Problem", render: (c) => <span className="tw-font-medium tw-text-content">{c.title}</span> },
          { key: "language", label: "Language", render: (c) => c.language || "—" },
          {
            key: "tests",
            label: "Tests passed",
            align: "right",
            render: (c) => (c.state === "done"
              ? <span className="tw-tabular-nums">{c.passed}/{c.total}</span>
              : <StatusBadge status={c.state === "pending" ? "warning" : c.state === "error" ? "danger" : "neutral"}>{CODING_STATE_LABELS[c.state] || "Not answered"}</StatusBadge>),
          },
          { key: "marks", label: "Marks", align: "right", render: (c) => <strong className="tw-tabular-nums">{c.marksAwarded}/{c.marks}</strong> },
        ]}
      />
    </AppCard>
  );
}

/**
 * AssessmentResultPage — `/s/assessment/result/:attemptId`: the score, the
 * score by section, the coding problems' verdicts and — when the assessment
 * allows it — the question-by-question review.
 *
 * Coding answers the server could not judge at submit time are PENDING; the
 * page says so and asks again every few seconds until they are judged.
 */
const AssessmentResultPage = () => {
  const { attemptId } = useParams();
  const navigate = useNavigate();

  const [result, setResult] = useState(null);
  const [state, setState] = useState("loading");
  const aliveRef = useRef(true);
  useEffect(() => () => { aliveRef.current = false; }, []);

  const passageById = useMemo(() => {
    const map = {};
    for (const p of result?.passages || []) map[String(p._id)] = p;
    return map;
  }, [result]);
  const ranges = useMemo(() => passageRanges(result?.questions), [result]);
  const loadPassageFile = useCallback(
    (passageId) => async () => {
      const { data } = await apiClient.get(
        `/student-assessments/attempt/${attemptId}/passages/${passageId}/file`
      );
      return data?.url || null;
    },
    [attemptId]
  );

  const fetchResult = useCallback(async () => {
    setState("loading");
    try {
      const { data } = await apiClient.get(`/student-assessments/attempt/${attemptId}/result`);
      setResult(data.result);
      setState(data.result ? "ready" : "missing");
    } catch (err) {
      setState(err?.response?.status === 404 ? "missing" : "error");
    }
  }, [attemptId]);

  useEffect(() => { fetchResult(); }, [fetchResult]);

  // While coding answers are being judged, ask again — quietly, for a while.
  const pending = (result?.codingPending || 0) > 0;
  useEffect(() => {
    if (!pending) return undefined;
    const started = Date.now();
    const timer = setInterval(async () => {
      if (Date.now() - started > PENDING_POLL_LIMIT_MS) {
        clearInterval(timer);
        return;
      }
      try {
        const { data } = await apiClient.get(`/student-assessments/attempt/${attemptId}/result`);
        if (aliveRef.current && data?.result) setResult(data.result);
      } catch {
        // The next tick tries again.
      }
    }, PENDING_POLL_MS);
    return () => clearInterval(timer);
  }, [pending, attemptId]);

  // Set by StudentTestSeriesDetailPage when a test is opened from within a series.
  const activeSeriesId = sessionStorage.getItem("vc-active-series");
  const back = (
    <div>
      <AppButton
        variant="ghost"
        size="sm"
        onClick={() => (activeSeriesId ? navigate(`/s/test-series/${activeSeriesId}`) : navigate("/s/assessments"))}
      >
        <ArrowLeft size={16} aria-hidden="true" /> {activeSeriesId ? "Back to Test Series" : "Back to Assessments"}
      </AppButton>
    </div>
  );

  if (state === "loading") {
    return (
      <div className="tw-mx-auto tw-flex tw-w-full tw-max-w-3xl tw-flex-col tw-gap-4" aria-busy="true" aria-label="Loading result">
        <Shimmer height={32} />
        <Shimmer height={220} />
        <Shimmer height={96} />
      </div>
    );
  }

  if (state !== "ready" || !result) {
    return (
      <div className="tw-mx-auto tw-flex tw-w-full tw-max-w-3xl tw-flex-col tw-gap-4">
        {back}
        {state === "error" ? (
          <Callout tone="danger" role="alert" action={<AppButton size="xs" variant="outline" onClick={fetchResult}>Try again</AppButton>}>
            The result could not be loaded.
          </Callout>
        ) : (
          <EmptyState icon={Trophy} title="Result not available" description="This attempt has no result to show yet." />
        )}
      </div>
    );
  }

  const a = result.assessment || {};
  const passed = result.passed;

  return (
    <div className="tw-mx-auto tw-flex tw-w-full tw-max-w-3xl tw-flex-col tw-gap-4">
      {back}

      {pending && (
        <Callout tone="info" role="status" icon={<Loader2 size={15} className="tw-animate-spin" />}>
          Your coding answers are being checked — your score will update in a minute.
        </Callout>
      )}

      <AppCard as="section" aria-label="Your score">
        <AppCardContent className="tw-flex tw-flex-col tw-items-center tw-gap-2 tw-text-center">
          <span
            aria-hidden="true"
            className={cn(
              "tw-mb-2 tw-flex tw-h-16 tw-w-16 tw-items-center tw-justify-center tw-rounded-full",
              passed ? "tw-bg-success-soft tw-text-success-fg" : "tw-bg-danger-soft tw-text-danger-fg",
            )}
          >
            {passed ? <Trophy size={32} /> : <XCircle size={32} />}
          </span>
          <span className="tw-text-[2.25rem] tw-font-bold tw-leading-none tw-tabular-nums tw-text-content">{result.percentage}%</span>
          <span className="tw-text-sm tw-text-content-muted">
            {result.score}/{result.totalMarks} marks · Attempt #{result.attemptNumber}
          </span>
          {a.passingPercentage > 0 && (
            <StatusBadge status={passed ? "passed" : "failed"} domain="assessmentResult" />
          )}
          {result.status === "auto_submitted" && (
            <span className="tw-text-[0.8125rem] tw-text-warning-fg">Submitted automatically — the time ran out or a rule was broken.</span>
          )}
          {result.status === "expired" && <span className="tw-text-[0.8125rem] tw-text-warning-fg">Time expired.</span>}
        </AppCardContent>
      </AppCard>

      <StatsGrid cols={4}>
        <StatsCard title="Correct" value={result.correctCount} icon={CheckCircle} iconColor="emerald" />
        <StatsCard title="Incorrect" value={result.incorrectCount} icon={XCircle} iconColor="rose" />
        <StatsCard title="Skipped" value={result.unansweredCount} icon={MinusCircle} iconColor="amber" />
        <StatsCard title="Time spent" value={formatDuration(result.timeSpent)} icon={Clock} iconColor="blue" />
      </StatsGrid>

      {a._id && (
        <div className="tw-flex tw-justify-center">
          <AppButton variant="outline" size="sm" onClick={() => navigate(`/s/assessment/${a._id}`)}>
            <RotateCcw size={16} aria-hidden="true" /> Try again
          </AppButton>
        </div>
      )}

      {result.sections?.length > 1 && <SectionsTable sections={result.sections} />}
      {result.coding?.length > 0 && <CodingCard coding={result.coding} />}

      {result.questions?.length > 0 && (
        <AppCard as="section" aria-labelledby="review-title">
          <AppCardHeader>
            <AppCardTitle as="h2" id="review-title">Question review</AppCardTitle>
          </AppCardHeader>
          <AppCardContent className="tw-flex tw-flex-col">
            {result.questions.map((q, idx) => {
              const answer = result.answers?.find(
                (ans) => ans.question?.toString() === q._id?.toString()
              );
              // The passage goes above the first question of its set.
              const pid = q.passage ? String(q.passage) : null;
              const passage = pid && ranges[pid]?.first === idx ? passageById[pid] : null;
              return (
                <React.Fragment key={q._id}>
                  {passage && (
                    <div className="tw-my-2 tw-rounded-md tw-border tw-border-solid tw-border-line tw-bg-surface-alt tw-p-4">
                      <PassageView
                        passage={passage}
                        rangeLabel={rangeLabel(ranges[pid])}
                        loadFileUrl={loadPassageFile(pid)}
                        cacheKey={`${attemptId}:${pid}`}
                        headingLevel={3}
                      />
                    </div>
                  )}
                  <ReviewQuestion
                    question={q}
                    answer={answer}
                    index={idx}
                    showCorrect={a.showCorrectAnswers}
                    showExplanation={a.showExplanation}
                  />
                </React.Fragment>
              );
            })}
          </AppCardContent>
        </AppCard>
      )}
    </div>
  );
};

export default AssessmentResultPage;
